import React from 'react';

const NODE_TYPES = [
    { value: 'menu', label: 'Menu', desc: 'Shows WhatsApp-native choices with buttons or a list.' },
    { value: 'message', label: 'Message', desc: 'Sends an information message (no choices)' },
    { value: 'handoff', label: 'Handoff', desc: 'Transfers the conversation to a human agent' },
];

const PRESENTATION_TYPES = [
    { value: 'buttons', label: 'Reply Buttons' },
    { value: 'list', label: 'List Message' },
    { value: 'text', label: 'Text Only' },
];

export default function NodeEditor({
    node,
    allNodes,
    onUpdate,
    onDelete,
    onClose,
    isRoot,
}) {
    if (!node) return null;

    const update = (field, value) => onUpdate(node.node_id, { [field]: value });
    const isMenuNode = node.type === 'menu';
    const isListPresentation = isMenuNode && (node.presentation || 'buttons') === 'list';

    const updateOption = (index, field, value) => {
        const opts = [...(node.options || [])];
        opts[index] = { ...opts[index], [field]: value };
        onUpdate(node.node_id, { options: opts });
    };

    const addOption = () => {
        const next = ((node.options || []).length + 1).toString();
        onUpdate(node.node_id, {
            options: [...(node.options || []), {
                input: `option_${next}`,
                label: `Option ${next}`,
                description: '',
                aliases: [],
                next_node: '',
            }],
        });
    };

    const removeOption = (index) => {
        onUpdate(node.node_id, {
            options: (node.options || []).filter((_, i) => i !== index),
        });
    };

    const currentType = NODE_TYPES.find(t => t.value === node.type);

    const updateType = (value) => {
        if (value === 'menu') {
            onUpdate(node.node_id, {
                type: value,
                presentation: node.presentation || 'buttons',
                action_title: node.action_title || 'View options',
                section_title: node.section_title || 'Options',
            });
            return;
        }

        onUpdate(node.node_id, {
            type: value,
            presentation: 'text',
            action_title: '',
            section_title: '',
            options: [],
            header_text: '',
            footer_text: '',
        });
    };

    return (
        <div className="node-editor">
            <div className="ne-header">
                <h3>Edit Node</h3>
                <button className="ne-close" onClick={onClose}>x</button>
            </div>

            <div className="ne-body">
                {/* Label */}
                <div className="ne-field">
                    <label>Node Name</label>
                    <input
                        type="text"
                        value={node.label || ''}
                        onChange={e => update('label', e.target.value)}
                        placeholder='e.g. "Main Menu"'
                    />
                </div>

                {/* Type */}
                <div className="ne-field">
                    <label>Type</label>
                    <select value={node.type} onChange={e => updateType(e.target.value)}>
                        {NODE_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    <div className="ne-type-hint">{currentType?.desc}</div>
                </div>

                {isMenuNode && (
                    <div className="ne-field">
                        <label>Presentation</label>
                        <select
                            value={node.presentation || 'buttons'}
                            onChange={e => update('presentation', e.target.value)}
                        >
                            {PRESENTATION_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Message */}
                <div className="ne-field">
                    <label>{isMenuNode ? 'Body Text' : 'Bot Message'}</label>
                    <textarea
                        value={node.message || ''}
                        onChange={e => update('message', e.target.value)}
                        rows={5}
                        placeholder={
                            node.type === 'menu'
                                ? "e.g.\nWelcome! How can we help today?\nChoose one of the options below."
                                : node.type === 'handoff'
                                    ? "e.g. Connecting you to an agent..."
                                    : "e.g. Our hours are Mon-Fri 9am-5pm."
                        }
                    />
                </div>

                {isMenuNode && node.presentation !== 'text' && (
                    <>
                        <div className="ne-field">
                            <label>Header Text</label>
                            <input
                                type="text"
                                value={node.header_text || ''}
                                onChange={e => update('header_text', e.target.value)}
                                placeholder='Optional header, e.g. "Account Services"'
                            />
                        </div>
                        <div className="ne-field">
                            <label>Footer Text</label>
                            <input
                                type="text"
                                value={node.footer_text || ''}
                                onChange={e => update('footer_text', e.target.value)}
                                placeholder='Optional footer, e.g. "Choose an option to continue"'
                            />
                        </div>
                    </>
                )}

                {isListPresentation && (
                    <>
                        <div className="ne-field">
                            <label>List Action Title</label>
                            <input
                                type="text"
                                value={node.action_title || ''}
                                onChange={e => update('action_title', e.target.value)}
                                placeholder='e.g. "More Services"'
                            />
                        </div>
                        <div className="ne-field">
                            <label>List Section Title</label>
                            <input
                                type="text"
                                value={node.section_title || ''}
                                onChange={e => update('section_title', e.target.value)}
                                placeholder='e.g. "Quick Services"'
                            />
                        </div>
                    </>
                )}

                {/* Handoff notify */}
                {node.type === 'handoff' && (
                    <div className="ne-field ne-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={node.notify_owner || false}
                                onChange={e => update('notify_owner', e.target.checked)}
                            />
                            Notify owner on handoff
                        </label>
                    </div>
                )}

                {/* Options — simplified: just trigger + target */}
                {node.type === 'menu' && (
                    <div className="ne-field">
                        <label>{isListPresentation ? 'List Rows' : 'Reply Options'}</label>
                        <p className="ne-options-hint">
                            Customers tap the option label. The option ID is the stable internal key used for routing.
                        </p>
                        <div className="ne-options">
                            {(node.options || []).map((opt, i) => (
                                <div key={i} className="ne-option-card">
                                    <div className="ne-option-row">
                                        <div className="ne-opt-field" style={{ flex: '0 0 120px' }}>
                                            <span className="ne-opt-label">Option ID</span>
                                            <input
                                                type="text"
                                                value={opt.input}
                                                onChange={e => updateOption(i, 'input', e.target.value)}
                                                placeholder="option_id"
                                            />
                                        </div>
                                        <div className="ne-opt-field" style={{ flex: 1 }}>
                                            <span className="ne-opt-label">Customer Label</span>
                                            <input
                                                type="text"
                                                value={opt.label || ''}
                                                onChange={e => updateOption(i, 'label', e.target.value)}
                                                placeholder="Shown on WhatsApp"
                                            />
                                        </div>
                                        <div className="ne-opt-field" style={{ flex: 1 }}>
                                            <span className="ne-opt-label">Goes to node</span>
                                            <select
                                                value={opt.next_node || ''}
                                                onChange={e => updateOption(i, 'next_node', e.target.value)}
                                            >
                                                <option value="">-- Select node --</option>
                                                {allNodes
                                                    .filter(n => n.node_id !== node.node_id)
                                                    .map(n => (
                                                        <option key={n.node_id} value={n.node_id}>
                                                            {n.label || n.node_id}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                        <button className="ne-opt-remove" onClick={() => removeOption(i)}>x</button>
                                    </div>
                                    {isListPresentation && (
                                        <div className="ne-option-row" style={{ marginTop: '8px' }}>
                                            <div className="ne-opt-field" style={{ flex: 1 }}>
                                                <span className="ne-opt-label">Description</span>
                                                <input
                                                    type="text"
                                                    value={opt.description || ''}
                                                    onChange={e => updateOption(i, 'description', e.target.value)}
                                                    placeholder="Optional list row description"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <button className="ne-add-option" onClick={addOption}>
                                + Add Option
                            </button>
                        </div>
                    </div>
                )}

                {/* Fallback */}
                <div className="ne-field">
                    <label>Fallback Node</label>
                    <select
                        value={node.fallback_node || ''}
                        onChange={e => update('fallback_node', e.target.value || null)}
                    >
                        <option value="">None (global fallback)</option>
                        {allNodes
                            .filter(n => n.node_id !== node.node_id)
                            .map(n => (
                                <option key={n.node_id} value={n.node_id}>
                                    {n.label || n.node_id}
                                </option>
                            ))}
                    </select>
                </div>

                {/* Delete */}
                {!isRoot && (
                    <button className="ne-delete" onClick={() => onDelete(node.node_id)}>
                        Delete This Node
                    </button>
                )}
            </div>
        </div>
    );
}
